import { _TrueTypeTableInfo, _TrueTypeHorizontalHeaderTable, _TrueTypeNameTable, _TrueTypeHeadTable, _TrueTypeOS2Table, _TrueTypePostTable, _TrueTypeCmapSubTable, _TrueTypeCmapTable, _TrueTypeAppleCmapSubTable, _TrueTypeMicrosoftCmapSubTable, _TrueTypeTrimmedCmapSubTable } from './ttf-table';
import { Dictionary } from '../pdf-primitives';
import { _StringTokenizer } from './string-layouter';
import { _TrueTypeCmapFormat, _TrueTypeCmapEncoding, _TrueTypePlatformID, _TrueTypeMicrosoftEncodingID, _TrueTypeMacintoshEncodingID, _TrueTypeCompositeGlyphFlag } from '../../core/enumerator';
export class _TrueTypeReader {
    constructor(fontData) {
        this._int32Size = 4;
        this._isFont = false;
        this._isMacTtf = false;
        this._isMacFont = false;
        this._missedGlyphs = 0;
        this._tableNames = ['cvt ', 'fpgm', 'glyf', 'head', 'hhea', 'hmtx', 'loca', 'maxp', 'prep'];
        this._entrySelectors = [0, 0, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4];
        this._fontData = fontData;
        this._initialize();
    }
    get macintosh() {
        if (this._macintoshDictionary === null || typeof this._macintoshDictionary === 'undefined') {
            this._macintoshDictionary = new Dictionary();
        }
        return this._macintoshDictionary;
    }
    get _microsoft() {
        if (this._microsoftDictionary === null || typeof this._microsoftDictionary === 'undefined') {
            this._microsoftDictionary = new Dictionary();
        }
        return this._microsoftDictionary;
    }
    get _macintoshGlyphs() {
        if (this._internalMacintoshGlyphs === null || typeof this._internalMacintoshGlyphs === 'undefined') {
            this._internalMacintoshGlyphs = new Dictionary();
        }
        return this._internalMacintoshGlyphs;
    }
    get _microsoftGlyphs() {
        if (this._internalMicrosoftGlyphs === null || typeof this._internalMicrosoftGlyphs === 'undefined') {
            this._internalMicrosoftGlyphs = new Dictionary();
        }
        return this._internalMicrosoftGlyphs;
    }
    _initialize() {
        if (typeof this._metrics === 'undefined' || this._metrics === null) {
            this._metrics = new _TrueTypeMetrics();
        }
        this._readFontDictionary();
        const nameTable = this._readNameTable();
        const headTable = this._readHeadTable();
        this._initializeFontName(nameTable);
        this._metrics._macStyle = headTable._macStyle;
    }
    _readFontDictionary() {
        this._offset = 0;
        this._check();
        const table = this._readInt16(this._offset);
        this._readInt16(this._offset);
        this._readInt16(this._offset);
        this._readInt16(this._offset);
        if (typeof this._tableDirectory === 'undefined' || this._tableDirectory === null) {
            this._tableDirectory = new Dictionary();
        }
        for (let i = 0; i < table; ++i) {
            const table = new _TrueTypeTableInfo();
            const tableKey = this._readString(this._int32Size);
            table._checksum = this._readInt32(this._offset);
            table._offset = this._readInt32(this._offset);
            table._length = this._readInt32(this._offset);
            this._tableDirectory.setValue(tableKey, table);
        }
        this._lowestPosition = this._offset;
        if (!this._isFont) {
            this._fixOffsets();
        }
    }
    _fixOffsets() {
        let minOffset = Number.MAX_VALUE;
        const tableKeys = this._tableDirectory.keys();
        for (let i = 0; i < tableKeys.length; i++) {
            const value = this._tableDirectory.getValue(tableKeys[i]);
            const offset = value._offset;
            if (minOffset > offset) {
                minOffset = offset;
                if (minOffset <= this._lowestPosition) {
                    break;
                }
            }
        }
        const shift = minOffset - this._lowestPosition;
        if (shift !== 0) {
            const table = new Dictionary();
            tableKeys.forEach((key) => {
                const value = this._tableDirectory.getValue(key);
                value._offset -= shift;
                table.setValue(key, value);
            });
            this._tableDirectory = table;
        }
    }
    _check() {
        let version = this._readInt32(this._offset);
        this._isMacTtf = (version === 0x74727565) ? true : false;
        if (version !== 0x10000 && version !== 0x74727565 && version !== 0x4f54544f) {
            this._isFont = true;
            this._offset = 0;
            const fontTag = this._readString(4);
            if (fontTag !== 'ttcf') {
                throw new Error('Can not read TTF font data');
            }
            this._offset += 4;
            const ttcIdentificationNumber = this._readInt32(this._offset);
            if (ttcIdentificationNumber < 0) {
                throw new Error('Can not read TTF font data');
            }
            this._offset = this._readInt32(this._offset);
            version = this._readInt32(this._offset);
        }
        return version;
    }
    _readNameTable() {
        const tableInfo = this._getTable('name');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypeNameTable();
        table._formatSelector = this._readUInt16(this._offset);
        table._recordsCount = this._readUInt16(this._offset);
        table._offset = this._readUInt16(this._offset);
        table._nameRecords = [];
        const recordSize = 12;
        let position = this._offset;
        for (let i = 0; i < table._recordsCount; i++) {
            this._offset = position;
            const record = new _TrueTypeNameRecord();
            record._platformID = this._readUInt16(this._offset);
            record._encodingID = this._readUInt16(this._offset);
            record._languageID = this._readUInt16(this._offset);
            record._nameID = this._readUInt16(this._offset);
            record._length = this._readUInt16(this._offset);
            record._offset = this._readUInt16(this._offset);
            this._offset = tableInfo._offset + table._offset + record._offset;
            const unicode = (record._platformID === 0 || record._platformID === 3);
            record._name = this._readString(record._length, unicode);
            table._nameRecords[i] = record;
            position += recordSize;
        }
        return table;
    }
    _readHeadTable() {
        const tableInfo = this._getTable('head');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypeHeadTable();
        table._version = this._readFixed(this._offset);
        table._fontRevision = this._readFixed(this._offset);
        table._checkSumAdjustment = this._readUInt32(this._offset);
        table._magicNumber = this._readUInt32(this._offset);
        table._flags = this._readUInt16(this._offset);
        table._unitsPerEm = this._readUInt16(this._offset);
        table._created = this._readInt64(this._offset);
        table._modified = this._readInt64(this._offset);
        table._xMin = this._readInt16(this._offset);
        table._yMin = this._readInt16(this._offset);
        table._xMax = this._readInt16(this._offset);
        table._yMax = this._readInt16(this._offset);
        table._macStyle = this._readUInt16(this._offset);
        table._lowestReadableSize = this._readUInt16(this._offset);
        table._fontDirectionHint = this._readInt16(this._offset);
        table._indexToLocalFormat = this._readInt16(this._offset);
        table._glyphDataFormat = this._readInt16(this._offset);
        return table;
    }
    _readHorizontalHeaderTable() {
        const tableInfo = this._getTable('hhea');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypeHorizontalHeaderTable();
        table._version = this._readFixed(this._offset);
        table._ascender = this._readInt16(this._offset);
        table._descender = this._readInt16(this._offset);
        table._lineGap = this._readInt16(this._offset);
        table._advanceWidthMax = this._readUInt16(this._offset);
        table._minLeftSideBearing = this._readInt16(this._offset);
        table._minRightSideBearing = this._readInt16(this._offset);
        table._xMaxExtent = this._readInt16(this._offset);
        table._caretSlopeRise = this._readInt16(this._offset);
        table._caretSlopeRun = this._readInt16(this._offset);
        this._offset += 10;
        table._metricDataFormat = this._readInt16(this._offset);
        table._numberOfHMetrics = this._readUInt16(this._offset);
        return table;
    }
    _readOS2Table() {
        const tableInfo = this._getTable('OS/2');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypeOS2Table();
        table._version = this._readUInt16(this._offset);
        table._xAvgCharWidth = this._readInt16(this._offset);
        table._usWeightClass = this._readUInt16(this._offset);
        table._usWidthClass = this._readUInt16(this._offset);
        table._fsType = this._readInt16(this._offset);
        table._ySubscriptXSize = this._readInt16(this._offset);
        table._ySubscriptYSize = this._readInt16(this._offset);
        table._ySubscriptXOffset = this._readInt16(this._offset);
        table._ySubscriptYOffset = this._readInt16(this._offset);
        table._ySuperscriptXSize = this._readInt16(this._offset);
        table._ySuperscriptYSize = this._readInt16(this._offset);
        table._ySuperscriptXOffset = this._readInt16(this._offset);
        table._ySuperscriptYOffset = this._readInt16(this._offset);
        table._yStrikeoutSize = this._readInt16(this._offset);
        table._yStrikeoutPosition = this._readInt16(this._offset);
        table._sFamilyClass = this._readInt16(this._offset);
        table._panose = this._readBytes(10);
        table._ulUnicodeRange1 = this._readUInt32(this._offset);
        table._ulUnicodeRange2 = this._readUInt32(this._offset);
        table._ulUnicodeRange3 = this._readUInt32(this._offset);
        table._ulUnicodeRange4 = this._readUInt32(this._offset);
        table._vendorIdentifier = this._readBytes(4);
        table._fsSelection = this._readUInt16(this._offset);
        table._usFirstCharIndex = this._readUInt16(this._offset);
        table._usLastCharIndex = this._readUInt16(this._offset);
        table._sTypoAscender = this._readInt16(this._offset);
        table._sTypoDescender = this._readInt16(this._offset);
        table._sTypoLineGap = this._readInt16(this._offset);
        table._usWinAscent = this._readUInt16(this._offset);
        table._usWinDescent = this._readUInt16(this._offset);
        table._ulCodePageRange1 = this._readUInt32(this._offset);
        table._ulCodePageRange2 = this._readUInt32(this._offset);
        if (table._version > 1) {
            table._sxHeight = this._readInt16(this._offset);
            table._sCapHeight = this._readInt16(this._offset);
            table._usDefaultChar = this._readUInt16(this._offset);
            table._usBreakChar = this._readUInt16(this._offset);
            table._usMaxContext = this._readUInt16(this._offset);
        }
        else {
            table._sxHeight = 0;
            table._sCapHeight = 0;
            table._usDefaultChar = 0;
            table._usBreakChar = 0;
            table._usMaxContext = 0;
        }
        return table;
    }
    _readPostTable() {
        const tableInfo = this._getTable('post');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypePostTable();
        table._formatType = this._readFixed(this._offset);
        table._italicAngle = this._readFixed(this._offset);
        table._underlinePosition = this._readInt16(this._offset);
        table._underlineThickness = this._readInt16(this._offset);
        table._isFixedPitch = this._readUInt32(this._offset);
        table._minType42 = this._readUInt32(this._offset);
        table._maxType42 = this._readUInt32(this._offset);
        table._minType1 = this._readUInt32(this._offset);
        table._maxType1 = this._readUInt32(this._offset);
        return table;
    }
    _readWidthTable(glyphCount, unitsPerEm) {
        const tableInfo = this._getTable('hmtx');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const width = [];
        for (let i = 0; i < glyphCount; i++) {
            const glyph = new _TrueTypeLongHorMetric();
            glyph._advanceWidth = this._readUInt16(this._offset);
            glyph._lsb = this._readInt16(this._offset);
            const glyphWidth = glyph._advanceWidth * 1000 / unitsPerEm;
            width.push(Math.floor(glyphWidth));
        }
        return width;
    }
    _readCmapTable() {
        const tableInfo = this._getTable('cmap');
        if (typeof tableInfo._offset !== 'undefined' && tableInfo._offset !== null) {
            this._offset = tableInfo._offset;
        }
        const table = new _TrueTypeCmapTable();
        table._version = this._readUInt16(this._offset);
        table._tablesCount = this._readUInt16(this._offset);
        let position = this._offset;
        const subTables = [];
        for (let i = 0; i < table._tablesCount; i++) {
            this._offset = position;
            const subTable = new _TrueTypeCmapSubTable();
            subTable._platformID = this._readUInt16(this._offset);
            subTable._encodingID = this._readUInt16(this._offset);
            subTable._offset = this._readUInt32(this._offset);
            position = this._offset;
            this._readCmapSubTable(subTable);
            subTables[i] = subTable;
        }
        return subTables;
    }
    _readCmapSubTable(subTable) {
        const tableInfo = this._getTable('cmap');
        this._offset = tableInfo._offset + subTable._offset;
        const format = this._readUInt16(this._offset);
        const encoding = this._getCmapEncoding(subTable._platformID, subTable._encodingID);
        if (encoding !== _TrueTypeCmapEncoding.unknown) {
            switch (format) {
                case _TrueTypeCmapFormat.apple:
                    this._readAppleCmapTable(subTable, encoding);
                    break;
                case _TrueTypeCmapFormat.microsoft:
                    this._readMicrosoftCmapTable(subTable, encoding);
                    break;
                case _TrueTypeCmapFormat.trimmed:
                    this._readTrimmedCmapTable(subTable, encoding);
                    break;
            }
        }
    }
    _readAppleCmapTable(subTable, encoding) {
        const tableInfo = this._getTable('cmap');
        this._offset = tableInfo._offset + subTable._offset;
        const table = new _TrueTypeAppleCmapSubTable();
        table._format = this._readUInt16(this._offset);
        table._length = this._readUInt16(this._offset);
        table._version = this._readUInt16(this._offset);
        if (this._maxMacIndex === null || typeof this._maxMacIndex === 'undefined') {
            this._maxMacIndex = 0;
        }
        for (let i = 0; i < 256; ++i) {
            const glyphInfo = new _TrueTypeGlyph();
            glyphInfo._index = this._readByte(this._offset);
            glyphInfo._width = this._getWidth(glyphInfo._index);
            glyphInfo._charCode = i;
            this.macintosh.setValue(i, glyphInfo);
            this._addGlyph(glyphInfo, encoding);
            this._maxMacIndex = Math.max(i, this._maxMacIndex);
        }
    }
    _readMicrosoftCmapTable(subTable, encoding) {
        const tableInfo = this._getTable('cmap');
        this._offset = tableInfo._offset + subTable._offset;
        const collection = (encoding === _TrueTypeCmapEncoding.unicode) ? this._microsoft
            : this.macintosh;
        const table = new _TrueTypeMicrosoftCmapSubTable();
        table._format = this._readUInt16(this._offset);
        table._length = this._readUInt16(this._offset);
        table._version = this._readUInt16(this._offset);
        table._segCountX2 = this._readUInt16(this._offset);
        table._searchRange = this._readUInt16(this._offset);
        table._entrySelector = this._readUInt16(this._offset);
        table._rangeShift = this._readUInt16(this._offset);
        const segCount = table._segCountX2 / 2;
        table._endCount = this._readUShortArray(segCount);
        table._reservedPad = this._readUInt16(this._offset);
        table._startCount = this._readUShortArray(segCount);
        table._idDelta = this._readUShortArray(segCount);
        table._idRangeOffset = this._readUShortArray(segCount);
        const length = (table._length / 2 - 8) - (segCount * 4);
        table._glyphID = this._readUShortArray(length);
        let codeOffset = 0;
        let index = 0;
        for (let j = 0; j < segCount; j++) {
            for (let k = table._startCount[j]; k <=
                table._endCount[j] && k !== 65535; k++) {
                if (table._idRangeOffset[j] === 0) {
                    codeOffset = (k + table._idDelta[j]) & 65535;
                }
                else {
                    index = j + table._idRangeOffset[j] / 2 - segCount +
                        k - table._startCount[j];
                    if (index >= table._glyphID.length) {
                        continue;
                    }
                    codeOffset = (table._glyphID[index] +
                        table._idDelta[j]) & 65535;
                }
                const glyph = new _TrueTypeGlyph();
                glyph._index = codeOffset;
                glyph._width = this._getWidth(glyph._index);
                const id = (encoding === _TrueTypeCmapEncoding.symbol) ? ((k & 0xff00) === 0xf000 ? k & 0xff : k) : k;
                glyph._charCode = id;
                collection.setValue(id, glyph);
                this._addGlyph(glyph, encoding);
            }
        }
    }
    _readTrimmedCmapTable(subTable, encoding) {
        const tableInfo = this._getTable('cmap');
        this._offset = tableInfo._offset + subTable._offset;
        const table = new _TrueTypeTrimmedCmapSubTable();
        table._format = this._readUInt16(this._offset);
        table._length = this._readUInt16(this._offset);
        table._version = this._readUInt16(this._offset);
        table._firstCode = this._readUInt16(this._offset);
        table._entryCount = this._readUInt16(this._offset);
        for (let i = 0; i < table._entryCount; ++i) {
            const glyphInfo = new _TrueTypeGlyph();
            glyphInfo._index = this._readUInt16(this._offset);
            glyphInfo._width = this._getWidth(glyphInfo._index);
            glyphInfo._charCode = i + table._firstCode;
            this.macintosh.setValue(i, glyphInfo);
            this._addGlyph(glyphInfo, encoding);
            this._maxMacIndex = Math.max(i, this._maxMacIndex);
        }
    }
    _initializeFontName(nameTable) {
        for (let i = 0; i < nameTable._recordsCount; i++) {
            const record = nameTable._nameRecords[i];
            if (record._nameID === 1) {
                this._metrics._fontFamily = record._name;
            }
            else if (record._nameID === 6) {
                this._metrics._postScriptName = record._name;
            }
            if (this._metrics._fontFamily !== null && typeof this._metrics._fontFamily !== 'undefined' &&
                this._metrics._postScriptName !== null && typeof this._metrics._postScriptName !== 'undefined') {
                break;
            }
        }
    }
    _getTable(name) {
        let table = new _TrueTypeTableInfo();
        let obj;
        if (this._tableDirectory.containsKey(name)) {
            obj = this._tableDirectory.getValue(name);
        }
        if (obj !== null && typeof obj !== 'undefined') {
            table = obj;
        }
        return table;
    }
    _getWidth(glyphCode) {
        glyphCode = (glyphCode < this._width.length) ? glyphCode : this._width.length - 1;
        return this._width[glyphCode];
    }
    _getCmapEncoding(platformID, encodingID) {
        let format = _TrueTypeCmapEncoding.unknown;
        if (platformID === _TrueTypePlatformID.microsoft &&
            encodingID === _TrueTypeMicrosoftEncodingID.undefined) {
            format = _TrueTypeCmapEncoding.symbol;
        }
        else if (platformID === _TrueTypePlatformID.microsoft
            && encodingID === _TrueTypeMicrosoftEncodingID.unicode) {
            format = _TrueTypeCmapEncoding.unicode;
        }
        else if (platformID === _TrueTypePlatformID.macintosh
            && encodingID === _TrueTypeMacintoshEncodingID.roman) {
            format = _TrueTypeCmapEncoding.macintosh;
        }
        return format;
    }
    _addGlyph(glyph, encoding) {
        let collection = null;
        switch (encoding) {
            case _TrueTypeCmapEncoding.unicode:
                collection = this._microsoftGlyphs;
                break;
            case _TrueTypeCmapEncoding.macintosh:
            case _TrueTypeCmapEncoding.symbol:
                collection = this._macintoshGlyphs;
                break;
        }
        if (glyph && typeof glyph._index !== 'undefined') {
            collection.setValue(glyph._index, glyph);
        }
    }
    _initializeMetrics(nameTable, headTable, horizontalHeadTable, os2Table, postTable, cmapTables) {
        this._initializeFontName(nameTable);
        let bSymbol = false;
        bSymbol = cmapTables.some((subTable) => {
            const encoding = this._getCmapEncoding(subTable._platformID, subTable._encodingID);
            return encoding === _TrueTypeCmapEncoding.symbol;
        });
        this._metrics._isSymbol = bSymbol;
        this._metrics._macStyle = headTable._macStyle;
        this._metrics._isFixedPitch = (postTable._isFixedPitch !== 0);
        this._metrics._italicAngle = postTable._italicAngle;
        const factor = 1000 / headTable._unitsPerEm;
        this._metrics._winAscent = os2Table._sTypoAscender * factor;
        this._metrics._macAscent = horizontalHeadTable._ascender * factor;
        this._metrics._capHeight = (os2Table._sCapHeight !== 0) ? os2Table._sCapHeight : 0.7 * headTable._unitsPerEm * factor;
        this._metrics._winDescent = os2Table._sTypoDescender * factor;
        this._metrics._macDescent = horizontalHeadTable._descender * factor;
        this._metrics._leading = (os2Table._sTypoAscender - os2Table._sTypoDescender + os2Table._sTypoLineGap) * factor;
        this._metrics._lineGap = Math.ceil(horizontalHeadTable._lineGap * factor);
        const left = headTable._xMin * factor;
        const top = Math.ceil(this._metrics._macAscent + this._metrics._lineGap);
        const right = headTable._xMax * factor;
        const bottom = this._metrics._macDescent;
        this._metrics._fontBox = [left, top, right, bottom];
        this._metrics._stemV = 80;
        this._metrics._widthTable = this._updateWidth();
        this._metrics._contains = this._tableDirectory.containsKey('CFF');
        this._metrics._subScriptSizeFactor = headTable._unitsPerEm / os2Table._ySubscriptYSize;
        this._metrics._superscriptSizeFactor = headTable._unitsPerEm / os2Table._ySuperscriptYSize;
    }
    _updateWidth() {
        const count = 256;
        const bytes = [];
        if (this._metrics._isSymbol) {
            for (let i = 0; i < count; i++) {
                const glyphInfo = this._getGlyph(String.fromCharCode(i));
                bytes[i] = (glyphInfo._empty) ? 0 : glyphInfo._width;
            }
        }
        else {
            const byteToProcess = [];
            const unknown = '?';
            const space = String.fromCharCode(32);
            for (let i = 0; i < count; i++) {
                byteToProcess[0] = i;
                const text = this._getString(byteToProcess, 0, byteToProcess.length);
                const ch = (text.length > 0) ? text[0] : unknown;
                let glyphInfo = this._getGlyph(ch);
                if (!glyphInfo._empty) {
                    bytes[i] = glyphInfo._width;
                }
                else {
                    glyphInfo = this._getGlyph(space);
                    bytes[i] = (glyphInfo._empty) ? 0 : glyphInfo._width;
                }
            }
        }
        return bytes;
    }
    _getDefaultGlyph() {
        const glyph = this._getGlyph(_StringTokenizer._whiteSpace);
        return glyph;
    }
    _getString(byteToProcess, start, length) {
        let result = '';
        for (let index = 0; index < length; index++) {
            result += String.fromCharCode(byteToProcess[index + start]);
        }
        return result;
    }
    _setOffset(offset) {
        this._offset = offset;
    }
    _readFontProgram(chars) {
        const glyphChars = this._getGlyphChars(chars);
        const locaTable = this._readLocaTable(this._bIsLocaShort);
        if (glyphChars && glyphChars._size() < chars._size()) {
            this._missedGlyphs = chars._size() - glyphChars._size();
        }
        this._updateGlyphChars(glyphChars, locaTable);
        const result1 = this._generateGlyphTable(glyphChars, locaTable, null, null);
        const glyphTableSize = result1.glyphTableSize;
        const newLocaTable = result1.newLocaTable;
        const newGlyphTable = result1.newGlyphTable;
        const result2 = this._updateLocaTable(newLocaTable, this._bIsLocaShort);
        const newLocaSize = result2.newLocaSize;
        const newLocaUpdated = result2.newLocaUpdated;
        const fontProgram = this._getFontProgram(newLocaUpdated, newGlyphTable, glyphTableSize, newLocaSize);
        return fontProgram;
    }
    _generateGlyphTable(glyphChars, locaTable, newLocaTable, newGlyphTable) {
        newLocaTable = [];
        const activeGlyphs = glyphChars.keys();
        activeGlyphs.sort((a, b) => a - b);
        let glyphSize = 0;
        activeGlyphs.forEach((glyphIndex) => {
            if (locaTable._offsets.length > 0) {
                glyphSize += locaTable._offsets[glyphIndex + 1] - locaTable._offsets[glyphIndex];
            }
        });
        const glyphSizeAligned = this._align(glyphSize);
        newGlyphTable = [];
        for (let i = 0; i < glyphSizeAligned; i++) {
            newGlyphTable.push(0);
        }
        let nextGlyphOffset = 0;
        let nextGlyphIndex = 0;
        const table = this._getTable('glyf');
        for (let i = 0; i < locaTable._offsets.length; i++) {
            newLocaTable.push(nextGlyphOffset);
            if (nextGlyphIndex < activeGlyphs.length && activeGlyphs[nextGlyphIndex] === i) {
                ++nextGlyphIndex;
                newLocaTable[i] = nextGlyphOffset;
                const oldGlyphOffset = locaTable._offsets[i];
                const oldNextGlyphOffset = locaTable._offsets[i + 1] - oldGlyphOffset;
                if (oldNextGlyphOffset > 0) {
                    this._offset = table._offset + oldGlyphOffset;
                    const result = this._read(newGlyphTable, nextGlyphOffset, oldNextGlyphOffset);
                    newGlyphTable = result.buffer;
                    nextGlyphOffset += oldNextGlyphOffset;
                }
            }
        }
        return { glyphTableSize: glyphSize, newLocaTable: newLocaTable, newGlyphTable: newGlyphTable };
    }
    _readLocaTable(bShort) {
        const tableInfo = this._getTable('loca');
        this._offset = tableInfo._offset;
        const table = new _TrueTypeLocaTable();
        let buffer = [];
        if (bShort) {
            const len = tableInfo._length / 2;
            buffer = [];
            for (let i = 0; i < len; i++) {
                buffer[i] = this._readUInt16(this._offset) * 2;
            }
        }
        else {
            const len = tableInfo._length / 4;
            buffer = [];
            for (let i = 0; i < len; i++) {
                buffer[i] = this._readUInt32(this._offset);
            }
        }
        table._offsets = buffer;
        return table;
    }
    _updateGlyphChars(glyphChars, locaTable) {
        if (!glyphChars.containsKey(0)) {
            glyphChars.setValue(0, 0);
        }
        const clone = new Dictionary();
        const glyphCharKeys = glyphChars.keys();
        glyphCharKeys.forEach((key) => {
            clone.setValue(key, glyphChars.getValue(key));
        });
        glyphCharKeys.forEach((nextKey) => {
            this._processCompositeGlyph(glyphChars, nextKey, locaTable);
        });
    }
    _processCompositeGlyph(glyphChars, glyph, locaTable) {
        if (glyph < locaTable._offsets.length - 1) {
            const glyphOffset = locaTable._offsets[glyph];
            if (glyphOffset !== locaTable._offsets[glyph + 1]) {
                const tableInfo = this._getTable('glyf');
                this._offset = tableInfo._offset + glyphOffset;
                const glyphHeader = new _TrueTypeGlyphHeader();
                glyphHeader.numberOfContours = this._readInt16(this._offset);
                glyphHeader.xMin = this._readInt16(this._offset);
                glyphHeader.yMin = this._readInt16(this._offset);
                glyphHeader.xMax = this._readInt16(this._offset);
                glyphHeader.yMax = this._readInt16(this._offset);
                if (glyphHeader.numberOfContours < 0) {
                    let skipBytes = 0;
                    const entry = true;
                    while (entry) {
                        const flags = this._readUInt16(this._offset);
                        const glyphIndex = this._readUInt16(this._offset);
                        if (!glyphChars.containsKey(glyphIndex)) {
                            glyphChars.setValue(glyphIndex, 0);
                        }
                        if ((flags & _TrueTypeCompositeGlyphFlag.MoreComponents) === 0) {
                            break;
                        }
                        skipBytes = ((flags & _TrueTypeCompositeGlyphFlag.Arg1And2AreWords) !== 0) ? 4 : 2;
                        if ((flags & _TrueTypeCompositeGlyphFlag.WeHaveScale) !== 0) {
                            skipBytes += 2;
                        }
                        else if ((flags & _TrueTypeCompositeGlyphFlag.WeHaveAnXyScale) !== 0) {
                            skipBytes += 4;
                        }
                        else if ((flags & _TrueTypeCompositeGlyphFlag.WeHaveTwoByTwo) !== 0) {
                            skipBytes += 2 * 4;
                        }
                        this._offset += skipBytes;
                    }
                }
            }
        }
    }
    _updateLocaTable(newLocaTable, bLocaIsShort) {
        const size = (bLocaIsShort) ? newLocaTable.length * 2 : newLocaTable.length * 4;
        const count = this._align(size);
        const writer = new _BigEndianWriter(count);
        newLocaTable.forEach((value) => {
            if (bLocaIsShort) {
                value /= 2;
                writer._writeShort(value);
            }
            else {
                writer._writeInt(value);
            }
        });
        return { newLocaUpdated: writer._data, newLocaSize: size };
    }
    _align(value) {
        return (value + 3) & (~3);
    }
    _getFontProgram(newLocaTableOut, newGlyphTable, glyphTableSize, locaTableSize) {
        const result = this._getFontProgramLength(newLocaTableOut, newGlyphTable, 0);
        const fontProgramLength = result.fontProgramLength;
        const table = result.table;
        const writer = new _BigEndianWriter(fontProgramLength);
        writer._writeInt(0x10000);
        writer._writeShort(table);
        const entrySelector = this._entrySelectors[table];
        writer._writeShort((1 << (entrySelector & 31)) * 16);
        writer._writeShort(entrySelector);
        writer._writeShort((table - (1 << (entrySelector & 31))) * 16);
        this._writeCheckSums(writer, table, newLocaTableOut, newGlyphTable, glyphTableSize, locaTableSize);
        this._writeGlyphs(writer, newLocaTableOut, newGlyphTable);
        return writer._data;
    }
    _getFontProgramLength(newLocaTableOut, newGlyphTable, table) {
        let fontProgramLength = 0;
        if (newLocaTableOut !== null && typeof newLocaTableOut !== 'undefined' && newLocaTableOut.length > 0 &&
            newGlyphTable !== null && typeof newGlyphTable !== 'undefined' && newGlyphTable.length > 0) {
            table = 2;
            const tableNames = this._tableNames;
            tableNames.forEach((tableName) => {
                if (tableName !== 'glyf' && tableName !== 'loca') {
                    const tableInfo = this._getTable(tableName);
                    if (!tableInfo._empty) {
                        ++table;
                        fontProgramLength += this._align(tableInfo._length);
                    }
                }
            });
            fontProgramLength += newLocaTableOut.length;
            fontProgramLength += newGlyphTable.length;
            const usedTablesSize = table * 16 + (3 * 4);
            fontProgramLength += usedTablesSize;
        }
        return { fontProgramLength: fontProgramLength, table: table };
    }
    _getGlyphChars(chars) {
        const dictionary = new Dictionary();
        if (chars !== null && typeof chars !== 'undefined') {
            const charKeys = chars.keys();
            charKeys.forEach((ch) => {
                const glyph = this._getGlyph(ch);
                if (!glyph._empty) {
                    dictionary.setValue(glyph._index, ch.charCodeAt(0));
                }
            });
        }
        return dictionary;
    }
    _writeCheckSums(writer, table, newLocaTableOut, newGlyphTable, glyphTableSize, locaTableSize) {
        if (writer !== null && typeof writer !== 'undefined' && newLocaTableOut !== null && typeof newLocaTableOut !== 'undefined' &&
            newLocaTableOut.length > 0 && newGlyphTable !== null && typeof newGlyphTable !== 'undefined' && newGlyphTable.length > 0) {
            const tableNames = this._tableNames;
            let usedTablesSize = table * 16 + (3 * 4);
            let nextTableSize = 0;
            tableNames.forEach((tableName) => {
                const tableInfo = this._getTable(tableName);
                if (tableInfo._empty) {
                    return;
                }
                writer._writeString(tableName);
                if (tableName === 'glyf') {
                    const checksum = this._calculateCheckSum(newGlyphTable);
                    writer._writeInt(checksum);
                    nextTableSize = glyphTableSize;
                }
                else if (tableName === 'loca') {
                    const checksum = this._calculateCheckSum(newLocaTableOut);
                    writer._writeInt(checksum);
                    nextTableSize = locaTableSize;
                }
                else {
                    writer._writeInt(tableInfo._checksum);
                    nextTableSize = tableInfo._length;
                }
                writer._writeUInt(usedTablesSize);
                writer._writeUInt(nextTableSize);
                usedTablesSize += this._align(nextTableSize);
            });
        }
    }
    _calculateCheckSum(bytes) {
        let pos = 0;
        let byte1 = 0;
        let byte2 = 0;
        let byte3 = 0;
        let byte4 = 0;
        let result = 0;
        if (bytes !== null && typeof bytes !== 'undefined' && bytes.length > 0) {
            for (let i = 0; i < (bytes.length + 1) / 4; i++) {
                byte4 += (bytes[pos++] & 255);
                byte3 += (bytes[pos++] & 255);
                byte2 += (bytes[pos++] & 255);
                byte1 += (bytes[pos++] & 255);
            }
            result = byte1;
            result += (byte2 << 8);
            result += (byte3 << 16);
            result += (byte4 << 24);
        }
        return result;
    }
    _writeGlyphs(writer, newLocaTable, newGlyphTable) {
        if (writer !== null && typeof writer !== 'undefined' && newLocaTable !== null && typeof newLocaTable !== 'undefined' &&
            newLocaTable.length > 0 && newGlyphTable !== null && typeof newGlyphTable !== 'undefined' && newGlyphTable.length > 0) {
            const tableNames = this._tableNames;
            tableNames.forEach((tableName) => {
                const tableInfo = this._getTable(tableName);
                if (tableInfo._empty) {
                    return;
                }
                if (tableName === 'glyf') {
                    writer._writeBytes(newGlyphTable);
                }
                else if (tableName === 'loca') {
                    writer._writeBytes(newLocaTable);
                }
                else {
                    const count = this._align(tableInfo._length);
                    const buff = [];
                    for (let i = 0; i < count; i++) {
                        buff.push(0);
                    }
                    this._offset = tableInfo._offset;
                    const result = this._read(buff, 0, tableInfo._length);
                    writer._writeBytes(result.buffer);
                }
            });
        }
    }
    _read(buffer, index, count) {
        let written = 0;
        if (buffer !== null && typeof buffer !== 'undefined' && buffer.length > 0) {
            let read = 0;
            do {
                for (let i = 0; (i < count - written) && (this._offset + i < this._fontData.length); i++) {
                    buffer[index + i] = this._fontData[this._offset + i];
                }
                read = count - written;
                this._offset += read;
                written += read;
            } while (written < count);
        }
        return { buffer: buffer, written: written };
    }
    _createInternals() {
        this._metrics = new _TrueTypeMetrics();
        const nameTable = this._readNameTable();
        const headTable = this._readHeadTable();
        this._bIsLocaShort = (headTable._indexToLocalFormat === 0);
        const horizontalHeadTable = this._readHorizontalHeaderTable();
        const os2Table = this._readOS2Table();
        const postTable = this._readPostTable();
        this._width = this._readWidthTable(horizontalHeadTable._numberOfHMetrics, headTable._unitsPerEm);
        const subTables = this._readCmapTable();
        this._initializeMetrics(nameTable, headTable, horizontalHeadTable, os2Table, postTable, subTables);
    }
    _getGlyph(charCode) {
        if (typeof charCode === 'number') {
            let obj1 = null;
            if (!this._metrics._isSymbol && this._microsoftGlyphs !== null) {
                if (this._microsoftGlyphs.containsKey(charCode)) {
                    obj1 = this._microsoftGlyphs.getValue(charCode);
                }
            }
            else if (this._metrics._isSymbol && this._macintoshGlyphs !== null) {
                if (this._macintoshGlyphs.containsKey(charCode)) {
                    obj1 = this._macintoshGlyphs.getValue(charCode);
                }
            }
            const glyph = (obj1 !== null) ? obj1 : this._getDefaultGlyph();
            return glyph;
        }
        else {
            let obj = null;
            let code = charCode.charCodeAt(0);
            if (!this._metrics._isSymbol && this._microsoft !== null) {
                if (this._microsoft.containsKey(code)) {
                    obj = this._microsoft.getValue(code);
                    if (code !== _StringTokenizer._whiteSpace.charCodeAt(0)) {
                        this._isFontPresent = true;
                    }
                }
                else if (code !== _StringTokenizer._whiteSpace.charCodeAt(0)) {
                    this._isFontPresent = false;
                }
            }
            else if (this._metrics._isSymbol && this.macintosh !== null || this._isMacFont) {
                if (this._maxMacIndex !== 0) {
                    code %= this._maxMacIndex + 1;
                }
                else {
                    code = ((code & 0xff00) === 0xf000 ? code & 0xff : code);
                }
                if (this.macintosh.containsKey(code)) {
                    obj = this.macintosh.getValue(code);
                    this._isFontPresent = true;
                }
            }
            if (charCode === _StringTokenizer._whiteSpace && obj === null) {
                obj = new _TrueTypeGlyph();
            }
            const glyph = (obj !== null) ? obj : this._getDefaultGlyph();
            return glyph;
        }
    }
    _readString(length, isUnicode) {
        if (typeof isUnicode === 'undefined' || isUnicode === null) {
            return this._readString(length, false);
        }
        else {
            let result = '';
            if (isUnicode) {
                for (let i = 0; i < length; i++) {
                    if (i % 2 !== 0) {
                        result += String.fromCharCode(this._fontData[this._offset]);
                    }
                    this._offset += 1;
                }
            }
            else {
                for (let i = 0; i < length; i++) {
                    result += String.fromCharCode(this._fontData[this._offset]);
                    this._offset += 1;
                }
            }
            return result;
        }
    }
    _readFixed(offset) {
        const integer = this._readInt16(offset);
        const sFraction = this._readInt16(offset + 2);
        const fraction = sFraction / 16384;
        return integer + fraction;
    }
    _readInt32(offset) {
        const i1 = this._fontData[offset + 3];
        const i2 = this._fontData[offset + 2];
        const i3 = this._fontData[offset + 1];
        const i4 = this._fontData[offset];
        this._offset += 4;
        return i1 + (i2 << 8) + (i3 << 16) + (i4 << 24);
    }
    _readUInt32(offset) {
        const i1 = this._fontData[offset + 3];
        const i2 = this._fontData[offset + 2];
        const i3 = this._fontData[offset + 1];
        const i4 = this._fontData[offset];
        this._offset += 4;
        return (i1 | i2 << 8 | i3 << 16 | i4 << 24);
    }
    _readInt16(offset) {
        let result = (this._fontData[offset] << 8) +
            this._fontData[offset + 1];
        result = result & (1 << 15) ? result - 0x10000 : result;
        this._offset += 2;
        return result;
    }
    _readInt64(offset) {
        const low = this._readInt32(offset + 4);
        let n = this._readInt32(offset) * 4294967296.0 + low;
        if (low < 0) {
            n += 4294967296;
        }
        return n;
    }
    _readUInt16(offset) {
        const result = (this._fontData[offset] << 8) |
            this._fontData[offset + 1];
        this._offset += 2;
        return result;
    }
    _readUShortArray(length) {
        const buffer = [];
        for (let i = 0; i < length; i++) {
            buffer[i] = this._readUInt16(this._offset);
        }
        return buffer;
    }
    _readBytes(length) {
        const result = [];
        for (let i = 0; i < length; i++) {
            result.push(this._fontData[this._offset]);
            this._offset += 1;
        }
        return result;
    }
    _readByte(offset) {
        const result = this._fontData[offset];
        this._offset += 1;
        return result;
    }
    _getCharacterWidth(code) {
        let glyphInfo = this._getGlyph(code);
        glyphInfo = (!glyphInfo._empty) ? glyphInfo : this._getDefaultGlyph();
        const codeWidth = (!glyphInfo._empty) ? glyphInfo._width : 0;
        return codeWidth;
    }
    _convertString(text) {
        let glyph = '';
        if (text !== null && text !== undefined && text.length > 0) {
            for (let k = 0; k < text.length; k++) {
                const ch = text[k];
                const glyphInfo = this._getGlyph(ch);
                if (!glyphInfo._empty) {
                    glyph += String.fromCharCode(glyphInfo._index);
                }
            }
        }
        return glyph;
    }
}
export class _TrueTypeNameRecord {
}
export class _TrueTypeMetrics {
    get _isItalic() {
        return ((this._macStyle & 2) !== 0);
    }
    get _isBold() {
        return ((this._macStyle & 1) !== 0);
    }
}
export class _TrueTypeLongHorMetric {
}
export class _TrueTypeGlyph {
    get _empty() {
        return (this._index === this._width && this._width === this._charCode && this._charCode === 0);
    }
}
export class _TrueTypeLocaTable {
}
export class _TrueTypeGlyphHeader {
}
export class _BigEndianWriter {
    constructor(capacity) {
        this.int32Size = 4;
        this.int16Size = 2;
        this.int64Size = 8;
        this._bufferLength = capacity;
        this._buffer = [];
    }
    get _data() {
        if (this._buffer.length < this._bufferLength) {
            const length = this._bufferLength - this._buffer.length;
            for (let i = 0; i < length; i++) {
                this._buffer.push(0);
            }
        }
        return this._buffer;
    }
    get _position() {
        if (typeof this._internalPosition === 'undefined' || this._internalPosition === null) {
            this._internalPosition = 0;
        }
        return this._internalPosition;
    }
    _writeShort(value) {
        const bytes = [((value & 0x0000ff00) >> 8), value & 0x000000ff];
        this._flush(bytes);
    }
    _writeInt(value) {
        const bytes = [(value & 0xff000000) >> 24, (value & 0x00ff0000) >> 16, (value & 0x0000ff00) >> 8, value & 0x000000ff];
        this._flush(bytes);
    }
    _writeUInt(value) {
        const buff = [(value & 0xff000000) >> 24, (value & 0x00ff0000) >> 16, (value & 0x0000ff00) >> 8, value & 0x000000ff];
        this._flush(buff);
    }
    _writeString(value) {
        if (value !== null && typeof value !== 'undefined') {
            const bytes = [];
            for (let i = 0; i < value.length; i++) {
                bytes.push(value.charCodeAt(i));
            }
            this._flush(bytes);
        }
    }
    _writeBytes(value) {
        this._flush(value);
    }
    _flush(buff) {
        if (buff !== null && typeof buff !== 'undefined') {
            let position = this._position;
            for (let i = 0; i < buff.length; i++) {
                this._buffer[position] = buff[i];
                position++;
            }
            this._internalPosition += buff.length;
        }
    }
}
